import { useCallback } from 'react'
import cn from 'classnames'
import { useArcVoiceCall } from '../../contexts/ArcVoiceCallContext'
import { useHuddle } from '../../contexts/HuddleContext'
import { ArcVoiceCallStatus } from '../../helpers/arcVoice/arcVoiceCall'
import { readyToTell } from '../../helpers/arcVoice/callAgenda'
import { isRunningOffStage, orbStateFor } from '../../helpers/arcVoice/callPresentation'
import { IArcVoiceLevels } from '../../helpers/arcVoice/realtimeVoiceClient'
import { HuddleSessionStatus } from '../../helpers/huddle/huddleSession'
import { useLiveClock } from '../../hooks/useCallClock'
import useOpenConversation from '../../hooks/useOpenConversation'
import { ArcVoiceOrb, ArcVoiceOrbState } from '../ArcVoice'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import styles from './Huddle.module.scss'
import HuddleDeviceMenu from './HuddleDeviceMenu'
import HuddleInviteButton from './HuddleInviteButton'
import HuddlePeers from './HuddlePeers'

interface IProps {
  /** The AIM panel exists here (desktop), so the conversation can open without leaving the page. */
  canUseBubble: boolean
}

const ARC_STATE_LABELS: Partial<Record<ArcVoiceOrbState, string>> = {
  [ArcVoiceOrbState.Connecting]: 'Joining…',
  [ArcVoiceOrbState.Speaking]: 'Speaking',
  [ArcVoiceOrbState.Thinking]: 'Working…',
}

const arcButtonTitle = (isHostingArc: boolean, isArcHere: boolean): string => {
  if (isHostingArc) return 'Send Arc away'
  if (isArcHere) return 'Arc is here through someone else'
  return 'Bring Arc into the huddle'
}

interface IHuddleClockProps {
  startedAt: string
}

/** Its own component, so the tick re-renders the clock and not the pill. */
const HuddleClock = ({ startedAt }: IHuddleClockProps) => {
  const clock = useLiveClock(Date.parse(startedAt))
  return <span className={styles.pillClock}>{clock}</span>
}

interface IHuddleArcChipProps {
  /** Null while Arc is hosted from another browser: there's no call here to show. */
  arcState: ArcVoiceOrbState | null
  getArcLevels: () => IArcVoiceLevels
}

const HuddleArcChip = ({ arcState, getArcLevels }: IHuddleArcChipProps) => (
  <span
    className={styles.arcChip}
    title="Arc is listening. Say “Arc, …” to ask it something."
    data-state={arcState ?? undefined}
  >
    {arcState ? (
      <ArcVoiceOrb state={arcState} getLevels={getArcLevels} size="sm" />
    ) : (
      <Icon name="sparkles" size="xs" />
    )}
    <Text variant="bodySm" component="span" casing="none" role="status">
      {arcState ? `Arc · ${ARC_STATE_LABELS[arcState] ?? 'Listening'}` : 'Arc'}
    </Text>
  </span>
)

/** The huddle you're in, wherever you are in the app: who's here, your mic, Arc, and the way out. */
const HuddlePill = ({ canUseBubble }: IProps) => {
  const { session, snapshot, leave, toggleMute, isHostingArc, inviteArc, dismissArc } = useHuddle()
  const arcCall = useArcVoiceCall()
  const openConversation = useOpenConversation(canUseBubble)

  const getLevels = useCallback(() => session?.levels ?? new Map<string, number>(), [session])
  const getArcLevels = useCallback(
    () => arcCall.call?.levels ?? { input: 0, output: 0 },
    [arcCall.call],
  )
  const handleLetArcTalk = useCallback((): void => {
    arcCall.call?.tellNow()
  }, [arcCall.call])
  const handleStopArcWork = useCallback((): void => {
    void arcCall.call?.stopWork()
  }, [arcCall.call])
  const channelId = snapshot?.channelId ?? null
  const handleReturn = useCallback(() => {
    if (channelId) openConversation(channelId)
  }, [channelId, openConversation])

  if (!snapshot) return null
  const { huddle, isMuted, peerId, connectedPeerIds, status } = snapshot
  const isArcHere = !!huddle?.arcHostUserId
  const isArcCallPillUp =
    !arcCall.roomHuddleId &&
    isRunningOffStage(arcCall.snapshot, arcCall.isStageVisible) &&
    arcCall.snapshot.status !== ArcVoiceCallStatus.Ended
  const arcState = isHostingArc && arcCall.snapshot ? orbStateFor(arcCall.snapshot) : null
  const hasArcAnswer =
    isHostingArc && !!arcCall.snapshot && readyToTell(arcCall.snapshot.agenda).length > 0
  const statusLabel =
    status === HuddleSessionStatus.Connecting
      ? 'Joining…'
      : `${huddle?.peers.length ?? 1} in huddle · transcribed`

  return (
    <div
      className={cn(styles.pill, { [styles.pillRaised]: isArcCallPillUp })}
      role="region"
      aria-label="Huddle"
    >
      <div className={styles.pillText}>
        <Text variant="title" component="span" className={styles.pillTitle}>
          Huddle
        </Text>
        <Text variant="bodySm" tone="secondary" casing="role" component="span" role="status">
          {statusLabel}
        </Text>
      </div>
      {huddle && (
        <HuddlePeers
          peers={huddle.peers}
          selfPeerId={peerId}
          connectedPeerIds={connectedPeerIds}
          getLevels={getLevels}
        />
      )}
      {isArcHere && <HuddleArcChip arcState={arcState} getArcLevels={getArcLevels} />}
      {hasArcAnswer && (
        <Button small secondary onClick={handleLetArcTalk} data-cy="huddle-let-arc-talk">
          Arc has an answer
        </Button>
      )}
      {arcCall.snapshot?.isArcWorking && isHostingArc && (
        <IconButton title="Stop what Arc is working on" box="sm" onClick={handleStopArcWork}>
          <Icon name="stop" size="sm" />
        </IconButton>
      )}
      {huddle ? (
        <HuddleClock startedAt={huddle.startedAt} />
      ) : (
        <span className={styles.pillClock} />
      )}
      <IconButton title="Open the conversation" box="sm" onClick={handleReturn}>
        <Icon name="window-open" size="sm" />
      </IconButton>
      {status === HuddleSessionStatus.Live && <HuddleInviteButton />}
      <HuddleDeviceMenu />
      <IconButton
        title={isMuted ? 'Unmute your mic' : 'Mute your mic'}
        box="sm"
        className={isMuted ? styles.pillMuted : undefined}
        onClick={toggleMute}
        aria-pressed={isMuted}
        disabled={status !== HuddleSessionStatus.Live}
      >
        <Icon name={isMuted ? 'mic-off' : 'mic'} size="sm" />
      </IconButton>
      <IconButton
        title={arcButtonTitle(isHostingArc, isArcHere)}
        box="sm"
        className={styles.pillArc}
        onClick={isHostingArc ? dismissArc : inviteArc}
        aria-pressed={isHostingArc}
        disabled={status !== HuddleSessionStatus.Live || (isArcHere && !isHostingArc)}
      >
        <Icon name="sparkles" size="sm" />
      </IconButton>
      <IconButton title="Leave huddle" box="sm" className={styles.pillEnd} onClick={leave}>
        <Icon name="phone" size="sm" />
      </IconButton>
    </div>
  )
}

export default HuddlePill
